import { useState, useRef } from 'react';
import { debounce } from '@wix/editor-elements-common-utils';
import { useDidUpdate } from '../useDidUpdate';
import { AnimationPhase, createAnimationState as _createAnimationState, } from './animationState';
export { AnimationPhase };
const getInitialAnimationStatesMap = (elementIds, currentElementId) => elementIds.reduce((idsMap, id) => {
    const phase = id === currentElementId
        ? AnimationPhase.EnterDone
        : AnimationPhase.ExitDone;
    return {
        ...idsMap,
        [id]: _createAnimationState(phase),
    };
}, {});
export const useSwitchAnimation = ({ elementIds, currentElementId, setCurrentElementId, getAnimationDuration, }) => {
    const createAnimationState = (phase) => _createAnimationState(phase, getAnimationDuration);
    const currentElementRef = useRef(currentElementId);
    const elementToEnterRef = useRef(null);
    const enteringElementRef = useRef(null);
    const exitingElementRef = useRef(null);
    const [elementIdToAnimationState, setElementIdToAnimationState] = useState(getInitialAnimationStatesMap(elementIds, currentElementId));
    const startExitAnimation = () => {
        exitingElementRef.current = currentElementRef.current;
        setElementIdToAnimationState(currentState => ({
            ...currentState,
            [exitingElementRef.current]: createAnimationState(AnimationPhase.ExitActive),
        }));
        setTimeout(handleExitDone, getAnimationDuration());
    };
    const switchToElement = (enteringElementId) => {
        elementToEnterRef.current = enteringElementId;
        if (!exitingElementRef.current) {
            startExitAnimation();
        }
    };
    const debouncedSwitchToElementRef = useRef(debounce(switchToElement, 400, {
        leading: true,
        trailing: true,
    }));
    const handleExitDone = () => {
        const stateUpdates = {};
        const terminateExitAnimation = () => {
            const exitedElementId = exitingElementRef.current;
            stateUpdates[exitedElementId] = createAnimationState(AnimationPhase.ExitDone);
            exitingElementRef.current = null;
        };
        const startNextEnterAnimation = () => {
            const elementToEnterId = elementToEnterRef.current;
            enteringElementRef.current = elementToEnterId;
            currentElementRef.current = elementToEnterId;
            setCurrentElementId(elementToEnterId); // Update externally
            stateUpdates[elementToEnterId] = createAnimationState(AnimationPhase.EnterActive);
            elementToEnterRef.current = null;
            setTimeout(handleEnterDone, getAnimationDuration());
        };
        terminateExitAnimation();
        startNextEnterAnimation();
        setElementIdToAnimationState(currentState => ({
            ...currentState,
            ...stateUpdates,
        }));
    };
    const handleEnterDone = () => {
        const enteredElementId = enteringElementRef.current;
        setElementIdToAnimationState(currentState => ({
            ...currentState,
            [enteredElementId]: createAnimationState(AnimationPhase.EnterDone),
        }));
        enteringElementRef.current = null;
        const elementToEnterId = elementToEnterRef.current;
        // Handle user switching element in the middle of enter animation
        if (elementToEnterId) {
            switchToElement(elementToEnterId);
        }
    };
    // Handle external change of current element
    useDidUpdate(() => {
        if (currentElementId !== currentElementRef.current) {
            switchToElement(currentElementId);
        }
    }, [currentElementId]);
    return [elementIdToAnimationState, debouncedSwitchToElementRef.current];
};
//# sourceMappingURL=useSwitchAnimation.js.map